import { useInjection } from 'inversify-react';
import { useCallback } from 'react';

import { DropdownNextOptionProps, Panel } from '@desygner/ui-common-components';
import { ZoomManager } from '@desygner/ui-editor-panels';

import { initial as zoomConfig } from '../../../../../config/zoom';
import { VirtualizerService } from '../../../../../core/viewer/virtualizer-service';
import { TYPES } from '../../../../../inversify.types';
import { IViewerStoreInjector } from '../../../../../store';
import {
  calculateZoomForFillToView,
  calculateZoomForFitToView,
} from '../../../../../utils/zoom.util';
import { StyledButtonSize } from '../BottomNav.styles';

const ZOOM_OPTIONS: Array<DropdownNextOptionProps> = [
  { label: '300%', value: String(zoomConfig.maxZoom) },
  { label: '200%', value: String(200) },
  { label: '125%', value: String(125) },
  { label: '100%', value: String(zoomConfig.defaultZoom) },
  { label: '75%', value: String(75) },
  { label: '50%', value: String(50) },
  { label: '25%', value: String(25) },
  { label: '10%', value: String(zoomConfig.minZoom) },
  { label: 'Fit', value: 'fit' },
  { label: 'Fill', value: 'fill' },
];

export const ZoomController = () => {
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const virtualizer = useInjection<VirtualizerService>(TYPES.Viewer.VirtualizerService);
  const setZoom = useViewerStore((state) => state.setZoom);

  const setViewOptions = useViewerStore((state) => state.setViewOptions);
  const isFitToView = useViewerStore((state) => state.viewOptions === 'fit');
  const isFillToView = useViewerStore((state) => state.viewOptions === 'fill');
  const pageDimension = useViewerStore((state) => state.pageDimension);
  const zoom = useViewerStore((state) => state.zoom);
  const { defaultZoom, maxZoom, minZoom, zoomStep } = zoomConfig;

  const zoomChangeHandler = useCallback(
    (zoom: number | string) => {
      const zoomAsNumber = Number(zoom);

      if (isNaN(zoomAsNumber) && zoom === 'fit') {
        const fitToViewPercentage = calculateZoomForFitToView(pageDimension);

        setZoom(fitToViewPercentage);
        setViewOptions('fit');
        const selectedPageNumber = useViewerStore.getState().selectedPageNumber;

        virtualizer.scrollToIndex(selectedPageNumber - 1);
        return;
      }

      if (isNaN(zoomAsNumber) && zoom === 'fill') {
        const fillToViewPercentage = calculateZoomForFillToView(pageDimension?.width);

        setZoom(fillToViewPercentage);
        setViewOptions('fill');
        return;
      }

      setViewOptions(null);
      setZoom(zoomAsNumber);
    },
    [pageDimension, setViewOptions, setZoom, virtualizer]
  );

  return (
    <StyledButtonSize>
      <Panel>
        <Panel.Content>
          <ZoomManager
            size="Small"
            initialValue={zoom ?? defaultZoom}
            onZoomChange={zoomChangeHandler}
            options={ZOOM_OPTIONS}
            fitZoom={isFitToView ? zoom : 0}
            fillZoom={isFillToView ? zoom : 0}
            max={maxZoom}
            min={minZoom}
            step={zoomStep}
            disablePortal
          />
        </Panel.Content>
      </Panel>
    </StyledButtonSize>
  );
};
